import type { FantasyTeamPlayer, Player } from '../../../types/graphql-generated';

// Roster view of FantasyTeamPlayer (without circular fantasyLeagueMember reference)
export type RosterTeamPlayer = Omit<FantasyTeamPlayer, 'fantasyLeagueMember'>;

// Whatever fills a lineup slot: a fantasy team player in a league, or a plain
// player in the admin "11 de la jornada" editor.
export interface LineupSlotOccupant {
  id: string;
  player: Player;
}

export interface LineupSlot<T extends LineupSlotOccupant = RosterTeamPlayer> {
  position: number;
  fantasyTeamPlayerId: string | null;
  fantasyTeamPlayer: T | null;
  fantasyScore?: number | null;
}

export const ROLE_LABELS: Record<string, string> = {
  'PORTERO': 'Portero',
  'DEFENSA': 'Defensa',
  'CENTROCAMPISTA': 'Centrocampista',
  'DELANTERO': 'Delantero',
};

export class LineupService {
  /**
   * Parses tactics string to get role distribution.
   * Example: "1-4-4-2" => { "PORTERO": 1, "DEFENSA": 4, "CENTROCAMPISTA": 4, "DELANTERO": 2 }
   *
   * @param tactics - Tactics string in format "G-D-M-F" (e.g., "1-4-4-2")
   * @returns Object mapping role enum values to number of players
   */
  static parseTactics(tactics: string): Record<string, number> {
    const parts = tactics.split('-');
    if (parts.length !== 4) return {};

    return {
      'PORTERO': parseInt(parts[0]),
      'DEFENSA': parseInt(parts[1]),
      'CENTROCAMPISTA': parseInt(parts[2]),
      'DELANTERO': parseInt(parts[3]),
    };
  }

  /**
   * Gets the role enum value for a given position based on tactics.
   *
   * @param position - Position number (1-11)
   * @param tactics - Tactics string (e.g., "1-4-4-2")
   * @returns Role enum value ('PORTERO', 'DEFENSA', 'CENTROCAMPISTA', or 'DELANTERO')
   */
  static getRoleForPosition(position: number, tactics: string): string {
    const distribution = this.parseTactics(tactics);

    let currentPosition = 1;

    // Goalkeeper (position 1)
    if (position === currentPosition) return 'PORTERO';
    currentPosition++;

    // Defenders
    for (let i = 0; i < distribution['DEFENSA']; i++) {
      if (position === currentPosition) return 'DEFENSA';
      currentPosition++;
    }

    // Midfielders
    for (let i = 0; i < distribution['CENTROCAMPISTA']; i++) {
      if (position === currentPosition) return 'CENTROCAMPISTA';
      currentPosition++;
    }

    // Forwards
    for (let i = 0; i < distribution['DELANTERO']; i++) {
      if (position === currentPosition) return 'DELANTERO';
      currentPosition++;
    }

    return 'PORTERO'; // Default to goalkeeper
  }

  /**
   * Updates a lineup based on new tactics by removing players that don't match the required role for their position.
   * Each position in the new tactics requires a specific role - if a player's role doesn't match, they are removed.
   *
   * @param currentLineup - Current lineup slots
   * @param newTactics - New tactics string (e.g., "1-3-5-2")
   * @returns Updated lineup with players removed if they don't match their position's required role
   */
  static updateLineupFromTactic<T extends LineupSlotOccupant>(
    currentLineup: LineupSlot<T>[],
    newTactics: string
  ): LineupSlot<T>[] {
    const updatedLineup = [...currentLineup];

    // Check each slot to see if the player's role matches the required role for that position
    currentLineup.forEach((slot, index) => {
      if (slot.fantasyTeamPlayer) {
        const playerRole = slot.fantasyTeamPlayer.player.role;
        const requiredRole = this.getRoleForPosition(slot.position, newTactics);

        // If the player's role doesn't match the required role for this position, remove them
        if (playerRole !== requiredRole) {
          updatedLineup[index] = {
            position: slot.position,
            fantasyTeamPlayerId: null,
            fantasyTeamPlayer: null,
          };
        }
      }
    });

    return updatedLineup;
  }

  /**
   * Initializes an array of 11 lineup slots and fills them with fantasy team players
   * that have a starting position.
   *
   * @param fantasyTeamPlayers - Array of fantasy team players from the query
   * @returns Array of 11 LineupSlot objects with starters filled in their positions
   */
  static initializeLineupSlotsFromFantasyTeamPlayers(
    fantasyTeamPlayers: RosterTeamPlayer[]
  ): LineupSlot[] {
    // Initialize all 11 positions with empty slots
    const initialLineup: LineupSlot[] = Array.from({ length: 11 }, (_, i) => ({
      position: i + 1,
      fantasyTeamPlayerId: null,
      fantasyTeamPlayer: null,
    }));

    // Fill in starters - find players with starting_position > 0
    fantasyTeamPlayers
      .filter((ftp) => ftp.startingPosition > 0 && ftp.startingPosition <= 11)
      .forEach((ftp) => {
        const index = ftp.startingPosition - 1;
        initialLineup[index] = {
          position: ftp.startingPosition,
          fantasyTeamPlayerId: ftp.id,
          fantasyTeamPlayer: ftp,
        };
      });

    return initialLineup;
  }
}
